// PATH: src/config/config.ts

// ---------- Types & Runtime config ----------
export type ApiService = "identity" | "tilmicode" | "ref" | "paiement";

type RuntimeConfig = Partial<{
    API_URL: string;
    API_BASE_URL: string; // on supporte les deux clés
    APP_VERSION: string;
    TILMIDPLUS_URL: string;
    API_TIMEOUT: string | number;
    MODE: string;
    // Surcharges optionnelles des préfixes par service côté runtime
    IDENTITY_PREFIX: string;
    TILMICODE_PREFIX: string;
    REF_PREFIX: string;
    PAIEMENT_PREFIX: string;
}>;

// 1) runtimeConfig éventuel injecté côté HTML (public/config.js)
const rc: RuntimeConfig =
    (typeof window !== "undefined" && (window as any).runtimeConfig) || {};

// 2) CRA (react-scripts): variables injectées au build
const env = (process.env as Record<string, string | undefined>) || {};

// ---------- Helpers ----------
const pickStr = (...vals: Array<string | undefined | null>): string => {
    for (const v of vals) {
        if (typeof v === "string" && v.trim() !== "") return v;
    }
    return "";
};

const pickNum = (...vals: Array<string | number | undefined | null>): number => {
    for (const v of vals) {
        if (typeof v === "number" && !Number.isNaN(v)) return v;
        if (typeof v === "string" && v.trim() !== "") {
            const n = Number(v);
            if (!Number.isNaN(n)) return n;
        }
    }
    return 100000; // défaut
};

const trimTrailingSlashes = (url: string) => url.replace(/\/+$/, "");

// ---------- Base host (commun à tous les services) ----------
export const apiBaseUrl: string = trimTrailingSlashes(
    pickStr(
        rc.API_URL,
        rc.API_BASE_URL, // support de API_BASE_URL si présent dans config.js
        env.REACT_APP_API_BASE_URL // ex: http://localhost:8080 ou https://mainapi.berouk.com
    )
);

// ---------- Préfixes par service (actifs hors développement) ----------
const isDev = rc.MODE === "dev";

// Surcharges possibles via runtimeConfig OU .env
const IDENTITY_PREFIX = pickStr(
    rc.IDENTITY_PREFIX,
    env.REACT_APP_IDENTITY_PREFIX,
    "/tc_identity"
);
const TILMICODE_PREFIX = pickStr(
    rc.TILMICODE_PREFIX,
    env.REACT_APP_TILMICODE_PREFIX,
    "/tc_tilmicodebe"
);
const REF_PREFIX = pickStr(
    rc.REF_PREFIX,
    env.REACT_APP_REF_PREFIX,
    "/tc_ref"
);
const PAIEMENT_PREFIX = pickStr(
    rc.PAIEMENT_PREFIX,
    env.REACT_APP_PAIEMENT_PREFIX,
    "/tc_paiement"
);

// Table de mapping des services → préfixes (ignorés en dev)
const SERVICE_PREFIX: Record<ApiService, string> = {
    identity: isDev ? "" : IDENTITY_PREFIX,
    tilmicode: isDev ? "" : TILMICODE_PREFIX,
    ref: isDev ? "" : REF_PREFIX,
    paiement: isDev ? "" : PAIEMENT_PREFIX,
};

// ---------- Constructeur d'URL par service ----------
export const buildBaseUrl = (service: ApiService): string => {
    const base = apiBaseUrl || "http://localhost:8080";
    const prefix = SERVICE_PREFIX[service] || "";
    return `${trimTrailingSlashes(base)}${prefix}`;
};

// Compatibilité historique : certains modules consomment apiPath directement.
export const apiPath = buildBaseUrl("tilmicode");

// ---------- Divers (inchangé) ----------
export const appVersion: string = pickStr(
    rc.APP_VERSION,
    env.REACT_APP_VERSION,
    "dev"
);

export const tilmidPlusURL: string = pickStr(
    rc.TILMIDPLUS_URL,
    env.REACT_APP_TC_CHAT_UI_URL,
    "https://api.tilmicode.com/"
);

export const apiTimeout: number = pickNum(
    rc.API_TIMEOUT as any,
    env.REACT_APP_API_TIMEOUT
);

// ---------- Alerte utile en dev ----------
if (!apiBaseUrl) {
    // eslint-disable-next-line no-console
    console.warn("[config] apiBaseUrl est vide. Vérifie public/config.js et .env");
}
